import { IReportOverview } from '@dadohr/shared/domainTypes/reports';
import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import moment from 'moment-timezone';
import { useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { useHoverDurationTracking } from '../../../../hooks/useHoverDurationTracking';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { ChartTitle } from '../ChartTitle';
import { GraphUnitSelector } from '../GraphUnitSelector';

const HEIGHT = 360;

const TooltipContainer = styled('div')`
  background-color: white;
  padding: 5px;
  border-radius: 5px;
  border: lightgray solid 1px;
`;

const CustomTooltip = ({
  filter,
  payload
}: {
  filter?: 'byWeek' | 'byMonth' | 'byCohort';
  payload?: any;
}) => {
  const { t } = useUnsafeTranslation();
  const date = payload[0]?.payload.value;

  if (!payload) {
    return null;
  }
  return (
    <>
      {(payload || []).length > 0 && (
        <TooltipContainer>
          <Typography variant="body1" color="textSecondary">
            {filter === 'byMonth'
              ? moment(date).format(
                  `[ 01 - ${moment(date).daysInMonth()}] MMMM YYYY`
                )
              : filter === 'byWeek'
              ? `${moment(date).startOf('isoWeek').format('DD')} - ${moment(
                  date
                )
                  .endOf('isoWeek')
                  .format('DD MMMM YYYY')}`
              : moment(date).format(`D MMMM YYYY`)}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t(`${payload[0].payload.count} new participants`)}
          </Typography>
        </TooltipContainer>
      )}
    </>
  );
};

export const PeopleBlockLineChart = ({
  height = HEIGHT,
  data
}: {
  height?: number;
  data: IReportOverview['data']['instances'];
}) => {
  const { t } = useUnsafeTranslation();
  const hoverTrackingProps = useHoverDurationTracking({
    name: 'Participants Chart Hovered',
    explanation:
      'Admin user hovered over the line chart showing the number of participants who started an experience per week/month/cohort'
  });
  const [selectedFilter, setSelectedFilter] = useState<
    'byWeek' | 'byMonth' | 'byCohort'
  >('byCohort');
  const chartData = data[selectedFilter];

  return (
    <>
      <FlexContainer justifyContent="space-between" marginBottom={4}>
        <ChartTitle
          title={t('Participants starting new experiences')}
          tooltipText={t(
            'Number of participants that started this experience in each cohort, week or month'
          )}
        />
        <GraphUnitSelector
          selectedFilter={selectedFilter}
          selectedValue={(value) => {
            setSelectedFilter(value);
          }}
          title="participants"
        />
      </FlexContainer>
      <div {...hoverTrackingProps}>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 40, left: 0, bottom: 5 }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="value"
              interval={
                chartData.length > 32
                  ? 'preserveStartEnd'
                  : chartData.length > 16
                  ? 1
                  : 0
              }
              tickMargin={20}
              tickSize={0}
              tickFormatter={(tk: any) => {
                switch (selectedFilter) {
                  case 'byCohort':
                    return moment(tk).format('DD MMM');
                  case 'byWeek':
                    return moment(tk).format(`DD MMM`);
                  case 'byMonth':
                    return moment(tk).format('MMMM');
                }
              }}
            />
            <YAxis axisLine={false} tickSize={0} tickMargin={20} />
            <Tooltip content={<CustomTooltip filter={selectedFilter} />} />
            <Line
              type="linear"
              dataKey="count"
              stroke="#0E78E4"
              dot={false}
              activeDot={{ stroke: '#0E78E4', strokeWidth: 2, r: 6 }}
              strokeWidth={3}
              animationDuration={750}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};
